import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

const HIGHLIGHT_DURATION_MS = 2500;

/**
 * Leser én konkret query-parameter (default `highlight`) og scroller +
 * highlighter raden som matcher når komponenten mountes med samme `id`.
 *
 * Brukermønster (typisk i en list-rad):
 *
 *   const ref = useHighlight(task.id);
 *   <tr ref={ref} ...>
 *
 * `id` kan være én id eller flere (array) — raden highlightes hvis query-
 * paramet matcher NOEN av dem. Brukes f.eks. for oppgave-rader som skal kunne
 * highlightes både på assignment-id og budget_line_id (sistnevnte deles av
 * medtildelte, jf. task_comment_added-varsler).
 *
 * Ved match scrolles raden inn i view (smooth, sentrert) og får en kort
 * highlight-effekt (yellow ring) i ~2,5 sekunder. Query-paramet fjernes fra
 * URL-en etterpå slik at refresh ikke trigger highlight på nytt.
 */
export function useHighlight<T extends HTMLElement = HTMLElement>(
  id: string | null | undefined | Array<string | null | undefined>,
  paramName: string = "highlight",
) {
  const ref = useRef<T | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const target = searchParams.get(paramName);
  const ids = Array.isArray(id) ? id : [id];
  const matches = !!target && ids.some((candidate) => candidate === target);

  useEffect(() => {
    if (!matches || !ref.current) return;
    const el = ref.current;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("notification-highlight");
    const timer = setTimeout(() => {
      el.classList.remove("notification-highlight");
      setSearchParams(
        (prev) => {
          prev.delete(paramName);
          return prev;
        },
        { replace: true },
      );
    }, HIGHLIGHT_DURATION_MS);
    return () => {
      clearTimeout(timer);
      el.classList.remove("notification-highlight");
    };
  }, [matches, paramName, setSearchParams]);

  return ref;
}
